/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import useStore from '@store/index';
import { useProducedAddis } from '../api/post-produced-addis';
import Decimal from 'decimal.js';
import { notifications } from '@mantine/notifications';
import {
	cloneDeep,
	removeEmptyArrays,
} from '@utils/functions';
import {
	TMesurabilityProject,
	TMesurabilityProjectQuaryParams,
} from '../types';
import {
	TQueryParams,
	TSelectOption,
} from '../../../types';
import { useTranslation } from 'react-i18next';
import { TParams } from '@global-types/app/app';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */
export const useOffsetProjectMesurability = () => {
	const {
		offsetMesurabilityProjectsSelected,
		setOffsetProjectMixinAco2,
		setOffsetCreationActiveStep,
		offsetCreationActiveStep,
		offsetMesurabilityProjects,
		setOffsetMesurabilityProjects,
		setOffsetMesurabilityProjectsSelected,
		offsetCreationDatas,
		offsetMesurabilityProjectsQueryParams,
		setOffsetMesurabilityProjectsPage,
		setOffsetMesurabilityProjectsQueryParams,
	} = useStore();

	const { t } = useTranslation();
	const producedAddis = useProducedAddis();

	/*************************************** Filters  ***************************************/
	const handleActiveFilter = (status: TQueryParams[]) => {
		setOffsetMesurabilityProjectsPage(1);
		const queryParamsActive = {
			active: status.map(
				(_status: TParams) => _status.value
			),
		};

		setOffsetMesurabilityProjectsQueryParams({
			...offsetMesurabilityProjectsQueryParams,
			...queryParamsActive,
		});
	};

	/**
	 * Filter list by project
	 *
	 * @param {TSelectOption} _projects
	 */
	const handleProjectFilter = (
		_projects: TSelectOption[]
	) => {
		setOffsetMesurabilityProjectsPage(1);
		const queryParamsProjects = {
			_id: _projects.map(
				(_project: TSelectOption) => _project.value
			),
		};

		const filterParams: TMesurabilityProjectQuaryParams =
			removeEmptyArrays({
				...offsetMesurabilityProjectsQueryParams,
				...queryParamsProjects,
			});
		setOffsetMesurabilityProjectsQueryParams(
			filterParams
		);
	};

	/**
	 * Filter list by country
	 *
	 * @param {TSelectOption} _countries
	 */
	const handleCountryFilter = (
		_countries: TSelectOption[]
	) => {
		setOffsetMesurabilityProjectsPage(1);

		const countries = _countries.map(
			(_country: TSelectOption) => _country.value
		);

		if (countries.length == 0) {
			const _offsetMesurabilityProjectsQueryParams =
				cloneDeep(offsetMesurabilityProjectsQueryParams);
			delete _offsetMesurabilityProjectsQueryParams[
				'location.countryCode'
			];
			setOffsetMesurabilityProjectsQueryParams(
				_offsetMesurabilityProjectsQueryParams
			);
			return;
		}
		const queryParamsCountries = {
			'location.countryCode': {
				$in: countries,
			},
		};
		const filterParams: TMesurabilityProjectQuaryParams =
			removeEmptyArrays({
				...offsetMesurabilityProjectsQueryParams,
				...queryParamsCountries,
			});
		setOffsetMesurabilityProjectsQueryParams(
			filterParams
		);
	};

	/**
	 * Filter list by technology
	 *
	 * @param {TSelectOption} _technologies
	 */
	const handleTechnologyFilter = (
		_technologies: TSelectOption[]
	) => {
		setOffsetMesurabilityProjectsPage(1);
		const queryParamsProjects = {
			technology: _technologies.map(
				(_technology: TSelectOption) => _technology.value
			),
		};

		const filterParams: TMesurabilityProjectQuaryParams =
			removeEmptyArrays({
				...offsetMesurabilityProjectsQueryParams,
				...queryParamsProjects,
			});
		setOffsetMesurabilityProjectsQueryParams(
			filterParams
		);
	};

	/**
	 * Check if project is selected
	 *
	 * @param {string} id
	 * @param {TMesurabilityProject} offsetTMesurabilityProjects
	 */
	const isSelectedProjectMesurability = (
		id: string,
		offsetTMesurabilityProjects: TMesurabilityProject[]
	) => {
		return offsetTMesurabilityProjects.some(
			(item: TMesurabilityProject) => item.id === id
		);
	};

	/**
	 * Navigate to next step
	 */
	const handleNextStep = () => {
		const _parsedOffsetMesurabilityProjectsSelected =
			offsetMesurabilityProjectsSelected.map(
				(item: TMesurabilityProject) => {
					const quantitytco2eq = new Decimal(
						item.quantitytco2eq
					);
					const million = new Decimal(1000000);
					const quantitytco2eqConverted =
						quantitytco2eq.mul(million);
					return {
						[item.id]: new Decimal(
							quantitytco2eqConverted
								.toNumber()
								.toFixed(3)
						).toNumber(),
					};
				}
			);

		const projectMixin_aco2 =
			_parsedOffsetMesurabilityProjectsSelected.reduce(
				(acc, item) => {
					const key = Object.keys(item)[0];
					const value = Object.values(item)[0];
					acc[key] = value;
					return acc;
				},
				{}
			);

		const bodyProducedAddis = {
			projectMixin_aco2: projectMixin_aco2,
		};

		producedAddis.mutate(bodyProducedAddis);
		setOffsetProjectMixinAco2(bodyProducedAddis);
		setOffsetCreationActiveStep(
			offsetCreationActiveStep + 1
		);
	};

	/**
	 * Change Tco2
	 *
	 * @param {TMesurabilityProject} _project
	 * @param {number} tco2
	 */
	const handleOnChangeTco2 = (
		project: TMesurabilityProject,
		tco2: number
	) => {
		let newOffsetMesurabilityProjects = [];
		let newOffsetMesurabilityProjectsSelected = [];
		const _cloneOffsetMesurabilityProjects = cloneDeep(
			offsetMesurabilityProjects
		);
		const _cloneOffsetMesurabilityProjectsSelected =
			cloneDeep(offsetMesurabilityProjectsSelected);

		const objectToUpdate =
			_cloneOffsetMesurabilityProjects.find(
				(obj: TMesurabilityProject) =>
					obj.id === project.id
			);

		const isSelectedProject =
			_cloneOffsetMesurabilityProjectsSelected.findIndex(
				(_currentProject: TMesurabilityProject) =>
					_currentProject.id == project.id
			) !== -1;

		if (!isSelectedProject) {
			setOffsetMesurabilityProjectsSelected([
				..._cloneOffsetMesurabilityProjectsSelected,
				{
					...project,
					quantitytco2eq: tco2,
				},
			]);
			newOffsetMesurabilityProjects =
				_cloneOffsetMesurabilityProjects.map(
					(project: TMesurabilityProject) =>
						project.id === objectToUpdate.id
							? {
									...project,
									quantitytco2eq: tco2,
								}
							: project
				);
			setOffsetMesurabilityProjects(
				newOffsetMesurabilityProjects
			);
			return;
		}

		if (objectToUpdate) {
			newOffsetMesurabilityProjects =
				_cloneOffsetMesurabilityProjects.map(
					(project: TMesurabilityProject) =>
						project.id === objectToUpdate.id
							? {
									...project,
									quantitytco2eq: tco2,
									// eslint-disable-next-line no-mixed-spaces-and-tabs
								}
							: project
				);
			setOffsetMesurabilityProjects(
				newOffsetMesurabilityProjects
			);

			newOffsetMesurabilityProjectsSelected =
				_cloneOffsetMesurabilityProjectsSelected.map(
					(project: TMesurabilityProject) =>
						project.id === objectToUpdate.id
							? {
									...project,
									quantitytco2eq: tco2,
									// eslint-disable-next-line no-mixed-spaces-and-tabs
								}
							: project
				);
			setOffsetMesurabilityProjectsSelected(
				newOffsetMesurabilityProjectsSelected
			);
		} else {
			newOffsetMesurabilityProjectsSelected =
				_cloneOffsetMesurabilityProjectsSelected.map(
					(_project: TMesurabilityProject) =>
						_project.id === project.id
							? {
									...project,
									quantitytco2eq: tco2,
									// eslint-disable-next-line no-mixed-spaces-and-tabs
								}
							: project
				);
			setOffsetMesurabilityProjectsSelected(
				newOffsetMesurabilityProjectsSelected
			);
		}
	};

	/**
	 * Select project
	 *
	 * @param {TMesurabilityProject} _project
	 */
	const handleAddProject = (
		project: TMesurabilityProject
	) => {
		if (
			Array.isArray(offsetMesurabilityProjectsSelected) &&
			offsetMesurabilityProjectsSelected.length > 3
		) {
			notifications.show({
				title: t('global.error'),
				message: t(
					'offsets.notifications.youCantSelectMoreThan4Projects'
				),
				color: 'error',
			});
		} else {
			const idExiste =
				offsetMesurabilityProjectsSelected.some(
					(_project: TMesurabilityProject) =>
						_project.id === project.id
				);

			if (idExiste) {
				notifications.show({
					title: t('global.error'),
					message: t(
						'offsets.notifications.thisProjectHasAlreadyBeenSelected'
					),
					color: 'error',
				});
			} else {
				const quantity = parseFloat(
					offsetCreationDatas.quantity
						? offsetCreationDatas.quantity
						: 0
				);

				const tokenAvailable = parseFloat(
					project.tokenAvailable
						? project.tokenAvailable
						: '0'
				);
				if (
					offsetMesurabilityProjectsSelected &&
					Array.isArray(
						offsetMesurabilityProjectsSelected
					) &&
					offsetMesurabilityProjectsSelected.length == 0
				) {
					if (tokenAvailable >= quantity) {
						const _fillQteProject = {
							...project,
							quantitytco2eq: quantity,
						};
						setOffsetMesurabilityProjectsSelected([
							...offsetMesurabilityProjectsSelected,
							{ ..._fillQteProject },
						]);

						const _cloneOffsetMesurabilityProjects =
							cloneDeep(offsetMesurabilityProjects);

						const objectToUpdate =
							_cloneOffsetMesurabilityProjects.find(
								(obj: TMesurabilityProject) =>
									obj.id === project.id
							);
						let newOffsetMesurabilityProjects = [];

						// Update global list
						if (objectToUpdate) {
							newOffsetMesurabilityProjects =
								_cloneOffsetMesurabilityProjects.map(
									(project: TMesurabilityProject) =>
										project.id === objectToUpdate.id
											? {
													...project,
													quantitytco2eq: quantity,
													// eslint-disable-next-line no-mixed-spaces-and-tabs
												}
											: project
								);
							setOffsetMesurabilityProjects(
								newOffsetMesurabilityProjects
							);
						}
					} else {
						const _fillQteProject = {
							...project,
							quantitytco2eq: tokenAvailable,
						};
						setOffsetMesurabilityProjectsSelected([
							...offsetMesurabilityProjectsSelected,
							{ ..._fillQteProject },
						]);
						const _cloneOffsetMesurabilityProjects =
							cloneDeep(offsetMesurabilityProjects);

						const objectToUpdate =
							_cloneOffsetMesurabilityProjects.find(
								(obj: TMesurabilityProject) =>
									obj.id === project.id
							);
						let newOffsetMesurabilityProjects = [];

						// Update global list
						if (objectToUpdate) {
							newOffsetMesurabilityProjects =
								_cloneOffsetMesurabilityProjects.map(
									(project: TMesurabilityProject) =>
										project.id === objectToUpdate.id
											? {
													...project,
													quantitytco2eq: tokenAvailable,
													// eslint-disable-next-line no-mixed-spaces-and-tabs
												}
											: project
								);
							setOffsetMesurabilityProjects(
								newOffsetMesurabilityProjects
							);
						}
					}
				} else {
					const _cloneOffsetMesurabilityProjectsSelected =
						cloneDeep(offsetMesurabilityProjectsSelected);
					const totalSelectedProjectsQte =
						_cloneOffsetMesurabilityProjectsSelected.reduce(
							(
								_acc: number,
								_project: TMesurabilityProject
							) => _acc + _project.quantitytco2eq,
							0
						);

					const _qte = new Decimal(
						offsetCreationDatas.quantity
					);
					const _totalSelectedProjectsQte = new Decimal(
						totalSelectedProjectsQte
					);

					let remainingQte = _qte
						.sub(_totalSelectedProjectsQte)
						.toNumber();

					// If the remaining qte is negative, set it to zero.
					if (remainingQte < 0) {
						remainingQte = 0;
					}

					let newProject = {
						...project,
						quantitytco2eq: remainingQte,
					};
					const _cloneOffsetMesurabilityProjects =
						cloneDeep(offsetMesurabilityProjects);

					const tokenAvailable =
						newProject.tokenAvailable;
					if (parseFloat(tokenAvailable) < remainingQte) {
						newProject = {
							...project,
							quantitytco2eq: parseFloat(tokenAvailable),
						};
					}

					const newListProjects =
						_cloneOffsetMesurabilityProjects.map(
							(project: TMesurabilityProject) =>
								project.id === newProject.id
									? newProject
									: project
						);

					setOffsetMesurabilityProjects(newListProjects);
					setOffsetMesurabilityProjectsSelected([
						...offsetMesurabilityProjectsSelected,
						{ ...newProject },
					]);
				}
			}
		}
	};

	/**
	 * Remove selected project
	 *
	 * @param {TMesurabilityProject} _project
	 */
	const handleRemoveSelectedProject = (
		_project: TMesurabilityProject
	) => {
		const idProject = _project.id;

		const cloneOffsetMesurabilityProjectsSelected =
			cloneDeep(offsetMesurabilityProjectsSelected);

		const cloneOffsetMesurabilityProjects = cloneDeep(
			offsetMesurabilityProjects
		);

		setOffsetMesurabilityProjectsSelected([
			...cloneOffsetMesurabilityProjectsSelected.filter(
				(_projectSelected: TMesurabilityProject) =>
					_projectSelected.id !== idProject
			),
		]);

		setOffsetMesurabilityProjects([
			...cloneOffsetMesurabilityProjects.map(
				(_projectSelected: TMesurabilityProject) => {
					if (_projectSelected.id === idProject) {
						return {
							..._projectSelected,
							quantitytco2eq: 0,
						};
					}
					return _projectSelected;
				}
			),
		]);
	};

	return {
		handleProjectFilter,
		handleCountryFilter,
		handleTechnologyFilter,
		isSelectedProjectMesurability,
		handleNextStep,
		handleOnChangeTco2,
		handleAddProject,
		handleRemoveSelectedProject,
		handleActiveFilter,
	};
};
